import {
    AmbientLight,
    Camera,
    Color,
    Group,
    Matrix4,
    Mesh,
    Object3D,
    OrthographicCamera,
    PerspectiveCamera,
    Scene,
    WebGLRenderer,
} from 'three';
import { NodeLoaderService } from '../../node-loader.service';
import { Viewer } from '../Viewer';
import { ViewerScene } from '../ViewerScene';
import { CameraUtils } from './CameraUtils';
import { Sides } from '../ViewCube';
import { HsbEntity, HsbModel, SceneLayerName } from '../../hsbcad-model';
import { HsbProject } from '../../hsb-project';
import { IProjectDB } from '../../iproject-db';
import { ShareData } from '../../isharedata';
import { Buffer } from 'buffer';
import  HSBControls  from '../Camera/HSBControls';

/**
 * The screen capture component is used to generate images or send images to the backend.
 */
export class ScreenCapture {
    /**
     * Returns the currently active scene in the viewer, null if no scene is active
     */
    public get currentScene(): ViewerScene | null | undefined {
        return this._viewer?.currentScene;
    }
    /**
     * Returns the currently active camera in the viewer, null if no camera is active
     */
    public get currentCamera(): Camera | null | undefined {
        return this._viewer?.currentCamera;
    }

    private _renderer: WebGLRenderer;

    /**
     * Whether the screen capture is in debug mode. Debug mode will open screen captures when they're done.
     */
    private _debug: boolean = false;
    /**
     * Whether the screen capture is in debug mode. Debug mode will open screen captures when they're done.
     */
    public get debug(): boolean {
        return this._debug;
    }
    /**
     * Whether the screen capture is in debug mode. Debug mode will open screen captures when they're done.
     */
    public set debug(v: boolean) {
        this._debug = v;
    }

    /**
     * ctor
     * @param _viewer The viewer to which this component should be bound
     * @param _renderer The renderer being used by the viewer
     * @param _cameraUtils The camera utility component used by the viewer
     * @param _nodeLoaderService The node loader service used by the client
     */
    constructor(
        private _viewer: Viewer,
        private _cameraUtils: CameraUtils,
        private _nodeLoaderService: NodeLoaderService
    ) {
        this._renderer = new WebGLRenderer({
            antialias: true,
        });
        this._renderer.setClearColor(new Color(0xe0dfda));
        this._renderer.setSize(1024, 576);
    }

    /**
     * Generates and uploads thumbnails for the project and for each model inside the project
     * @param project The project for which to update the thumbnails
     * @param projectDB The projectDB for which to update the thumbnails
     */
    public async updateThumbnail(project: HsbProject, projectDB: IProjectDB) {
        try {
            if (!this.currentScene || Object.keys(project.HsbModels).length === 0) {
                // Early out, we need the scene active and/or a model to show
                return;
            }
            // Project thumbnail
            let projectImageMQ = await this.requestScreenshot({
                target: this.currentScene.GetLayer(SceneLayerName.Entities),
                sides: Sides.Front | Sides.Top | Sides.Left,
                quality: 'low',
            });
            if (this.debug) this.openImage(projectImageMQ, 'Project ' + projectDB.name);
            // Convert to blob
            const projectBlob = await this.convertDataURIToBlob(projectImageMQ);
            const projectPath = project.datastoreFolder + '/thumbnail.png';
            await this._nodeLoaderService.addFile(projectPath, projectBlob);
            // Request to backend
            let nodeModelData = {
                _id: projectDB.nodeModel._id,
                thumbnailDataUrl: projectImageMQ,
            };
            await this._nodeLoaderService.hsbModelAPIBackService
                .updateProjectModelNode(nodeModelData)
                .catch((error) => {
                    console.error(
                        'Something went wrong while updating the project thumbnail!',
                        error
                    );
                });

            // Setting up per model
            let entityLayer = this.currentScene.GetLayer(SceneLayerName.Entities);
            entityLayer.children.forEach((c) => (c.visible = false));
            // Model thumbnails

            var p = Promise.resolve(true);
            Object.entries(project.HsbModels).forEach(([key, model]: [string, any]) => {
                p = p.then(() => this.UpdateModelThumbnail(project, key, model, entityLayer));
                return p;
            });
            p.finally(() => {
                entityLayer.children.forEach((c) => (c.visible = true));
            });
            return p;
        } catch (err: any) {
            console.error('Could not create thumbnail: ' + err.message);
        }
    }

    private openImage(imageURI: string, title?: string) {
        var image = new Image();
        image.src = imageURI;
        var w = window.open('', title ?? 'Image');
        if (w) {
            w.document.write(title ?? 'Thumbnail' + '\n');
            w.document.write(image.outerHTML);
        }
    }

    /**
     * Generates and updates the model thumbnails on the backend
     * @param project The project to which the models bleong
     * @param key The modelID of the model
     * @param model The model for which to take a sceenshot
     * @param entityLayer The root object for this model
     * @returns Promise that resolves true if it succeeded, false otherwise
     */
    private async UpdateModelThumbnail(
        project: HsbProject,
        key: string,
        model: any,
        entityLayer: THREE.Object3D
    ) {
        let modelNodeData = model.Node.metadata.data;
        // Getting 3D object
        let modelObject = entityLayer.children.find((c) => c.name === 'Root: ' + key);
        if (!modelObject) return false;

        // Set visible
        modelObject.visible = true;
        // Get dataURI
        let modelImageLQ = await this.requestScreenshot({
            sides: Sides.Front | Sides.Top | Sides.Left,
            target: modelObject,
            quality: 'low',
        });
        if (this.debug) this.openImage(modelImageLQ, 'Model ' + model.modelId);
        // Hide again
        // Set visible
        modelObject.visible = false;
        // Converting to blob
        const modelBlob = await this.convertDataURIToBlob(modelImageLQ);
        // Set path
        let path = [project.datastoreFolder, model.Node.pathid].join('/') + '/thumbnail.png';

        try {
            await this._nodeLoaderService.addFile(path, modelBlob);
            if (!modelNodeData.documents.find((d) => d.path === path))
                modelNodeData.documents.push({ path: path, provider: 'hsbShare' });
            modelNodeData.thumbnailDataUrl = modelImageLQ;
            let shareData = new ShareData(
                model.Node,
                project,
                'hsbshare.projectmodel',
                modelNodeData
            );
            await this._nodeLoaderService.addMetaFileDataDB(modelNodeData, 'PUT', shareData);
            return true;
        } catch (err) {
            console.error('Could not update file for model ' + key);
            return false;
        }
    }

    /**
     * Converts the given data uri into a Blob
     * @param dataURI THe data URI to convert into a blob
     * @returns The converted image as a Blob
     */
    public async convertDataURIToBlob(dataURI: string) {
        const blob = await (await fetch(dataURI)).blob();
        return blob;
    }

    /**
     * Generates and returns a data URI string for a screenshot.
     * @param entities The desired entities of which to take a screenshot
     * @param options (Optional) The desired options for the screenshot
     * @returns The data string for the screenshot
     */
    public screenshotEntities(
        entities: HsbEntity[],
        options: Partial<Omit<ScreenshotOptions, 'target' | 'scene'>> = {
            sides: Sides.Top | Sides.Front | Sides.Left,
            quality: 'medium',
        }
    ) {
        if (!(entities?.length > 0)) return Promise.reject('No entities provided!');

        let activeScene = this._viewer.currentScene;
        if (!activeScene) return Promise.reject('No active scene!');
        let self = this;

        let clone = new ViewerScene(
            this._viewer._width,
            this._viewer._height,
            this._viewer._renderer,
            { picker: this._viewer._picker }
        );
        clone.copy(activeScene, false);
        clone.name = 'Screenshot scene';
        clone.light = new AmbientLight(0xffffe0, 0.95);
        clone.add(clone.light);

        // Getting relevant objects
        let objects = entities.map((e) => e.object).filter((o) => !!o) as Object3D[];
        // Settting matrices
        let clonedObjects: Object3D[] = objects.map((o) => {
            let cloneObject = o.clone(true);
            if (o instanceof Mesh && cloneObject instanceof Mesh)
                cloneObject.material = o.userData.originalmaterial ?? o.material;
            cloneObject.matrix.copy(o.matrixWorld);
            clone.GetLayer(SceneLayerName.Entities).add(cloneObject);
            return cloneObject;
        });
        // Getting screenshot
        let screenshotPromise = self
            .requestScreenshot({
                target: clonedObjects,
                scene: clone,
                ...options,
            })
            .then((result) => {
                if (this.debug) self.openImage(result as string, 'Requested screenshot');
                return Promise.resolve(result);
            });
        screenshotPromise.finally(() => {
            clone.onDispose();
        });
        return screenshotPromise;
    }

    /**
     * Renders and returns a screenshot of per the given options.
     * @param options options detailing how the screenshot should be taken
     * @returns A promise that resolves with the image if taken, rejects otherwise
     */
    public requestScreenshot(options?: Partial<ScreenshotOptions>): Promise<string> {
        let { target, sides, direction, cameraType, cameraProps, quality, scene } = options ?? {};
        let self = this;
        scene ??= this._viewer.currentScene;
        if (!cameraType)
            // By default camera is orthographic
            cameraType = 'Orthographic';

        if (!scene) return Promise.reject('Scene is necessary for a screenshot!');
        // Creating temp camera
        var camera = new OrthographicCamera(-512, 512, 288, -288, this._viewer.currentCamera?.near ?? 0.001, this._viewer.currentCamera?.far ?? 100000);
        if (!camera) return Promise.reject('Could not copy camera');
        var controls = new HSBControls(camera, this._renderer.domElement);
        camera.name = 'Screen Capture Camera';
        camera.layers.enableAll();
        scene.add(camera);
        var qualityInt = 1; // Default quality = 100%
        // Setting quality
        switch (quality) {
            case 'high':
                qualityInt = 1;
                break;
            case 'medium':
                qualityInt = 0.5;
                break;
            case 'low':
                qualityInt = 0.1;
                break;
            default:
                qualityInt = 1;
        }
        // Lighting

        var out = new Promise<string>(async (resolve, reject) => {
            try {
                // Camera orientation
                if (target) {
                    await self._cameraUtils.FocusCameraOnObject(target, {
                        sides,
                        direction,
                        camera,
                        controls,
                        scene: scene!,
                    });
                    // Render
                    self._renderer.render(scene!, camera!);
                } else {
                    await self._cameraUtils.FocusCameraInDirection({
                        sides,
                        direction,
                        camera,
                        controls,
                        scene: scene!,
                    });
                    self._renderer.render(scene!, camera!);
                }
                // Image creation
                let image = self._renderer.domElement.toDataURL('image/png', qualityInt);
                if (image) resolve(image);
                else reject('Could not create an image');
            } catch (err: any) {
                reject(err.message);
            }
        });
        // Remove camera after creating image
        out.finally(() => {
            scene!.remove(camera!);
            controls.dispose();
        });
        return out;
    }
}

type CameraType = 'Orthographic' | 'Perspective';
type CameraProps<T extends CameraType> = T extends 'Orthographic'
    ? OrthographicCameraProps
    : T extends 'Perspective'
    ? PerspectiveCameraProps
    : never;

interface OrthographicCameraProps {
    width: number;
    height: number;
    near: number;
    far: number;
}
interface PerspectiveCameraProps {
    fov: number;
    near: number;
    far: number;
    aspect: number;
}

export interface ScreenshotOptions<T extends CameraType = 'Orthographic'> {
    target: THREE.Object3D | THREE.Object3D[];
    sides: Sides;
    direction: THREE.Vector3;
    quality: 'high' | 'medium' | 'low';
    cameraType: T;
    cameraProps: CameraProps<T>;
    scene?: ViewerScene | null;
}
